<template>
  <div class="stat-tile" :class="[`stat-tile--${order}`, `stat-tile--${density}`]">
    <div class="stat-tile__label">{{ label }}</div>
    <div class="stat-tile__value" :class="`stat-tile__value--${tone}`">{{ value }}</div>
    <div v-if="sub" class="stat-tile__sub">{{ sub }}</div>
  </div>
</template>

<script setup>
// One figure in the sale's stats block: a label, a big tabular number, and an
// optional supporting line.
//
// `value` and `sub` are pre-formatted strings. Deciding that total sales reads
// "$412k" and not "$412,300" is a presentation choice the stats panel makes
// once for the whole block, and a tile that formatted its own would have to be
// told the units anyway.
defineOptions({ name: 'StatTile' });

defineProps({
  label: { type: String, required: true },
  // Pre-formatted by the caller.
  value: { type: [String, Number], required: true },
  sub: { type: String, default: null },
  // neutral | success | warning | danger | info — tints the figure only.
  tone: {
    type: String,
    default: 'neutral',
    validator: (value) => ['neutral', 'success', 'warning', 'danger', 'info'].includes(value),
  },
  // Which reads first. `label-first` is the stats block's eyebrow over a
  // figure. `value-first` is a scoreboard — the number on top, centered, its
  // word under it in sentence case — the way the phone's Bid Stack counts Won /
  // Lost / Budget. The DOM order stays label → value either way, so a screen
  // reader hears "Won, 3" in both; only the drawing flips.
  order: {
    type: String,
    default: 'label-first',
    validator: (value) => ['label-first', 'value-first'].includes(value),
  },
  // `condensed` puts the figure and its word on one baseline at a third of the
  // height: the score row that pins under the notch once the full row has
  // scrolled away.
  density: {
    type: String,
    default: 'regular',
    validator: (value) => ['regular', 'condensed'].includes(value),
  },
});
</script>

<style scoped>
.stat-tile {
  flex: 1;
  min-width: 0;
  background: var(--p-stat-tile-background);
  border: var(--p-stat-tile-border-width) solid var(--p-stat-tile-border-color);
  border-radius: var(--p-stat-tile-border-radius);
  padding: var(--p-stat-tile-padding);
  display: flex;
  flex-direction: column;
  gap: var(--p-stat-tile-gap);
}

.stat-tile__label {
  font-size: var(--p-stat-tile-label-font-size);
  font-weight: var(--p-stat-tile-label-font-weight);
  letter-spacing: var(--p-stat-tile-label-letter-spacing);
  color: var(--p-stat-tile-label-color);
  text-transform: uppercase;
}

.stat-tile__value {
  font-family: var(--p-stat-tile-value-font-family);
  font-weight: var(--p-stat-tile-value-font-weight);
  font-size: var(--p-stat-tile-value-font-size);
  letter-spacing: var(--p-stat-tile-value-letter-spacing);
  line-height: var(--p-stat-tile-value-line-height);
  font-variant-numeric: tabular-nums;
}

.stat-tile__value--neutral {
  color: var(--p-stat-tile-value-neutral-color);
}

.stat-tile__value--success {
  color: var(--p-stat-tile-value-success-color);
}

.stat-tile__value--warning {
  color: var(--p-stat-tile-value-warning-color);
}

.stat-tile__value--danger {
  color: var(--p-stat-tile-value-danger-color);
}

.stat-tile__value--info {
  color: var(--p-stat-tile-value-info-color);
}

.stat-tile__sub {
  font-size: var(--p-stat-tile-sub-font-size);
  color: var(--p-stat-tile-sub-color);
}

/* Scoreboard: number over word, centered. `order` moves the boxes without
   touching the DOM, so the reading order stays label → value. */
.stat-tile--value-first {
  align-items: center;
  text-align: center;
  gap: var(--p-stat-tile-value-first-gap);
  padding: var(--p-stat-tile-value-first-padding);
  border-radius: var(--p-stat-tile-value-first-border-radius);
}

.stat-tile--value-first .stat-tile__value { order: 1; }
.stat-tile--value-first .stat-tile__label {
  order: 2;
  text-transform: none;
  font-size: var(--p-stat-tile-value-first-label-font-size);
  font-weight: var(--p-stat-tile-value-first-label-font-weight);
  letter-spacing: var(--p-stat-tile-value-first-label-letter-spacing);
  color: var(--p-stat-tile-value-first-label-color);
}
.stat-tile--value-first .stat-tile__sub { order: 3; }

/* Condensed: one baseline, a third of the height. */
.stat-tile--condensed {
  flex-direction: row;
  align-items: baseline;
  justify-content: center;
  gap: var(--p-stat-tile-condensed-gap);
  padding: var(--p-stat-tile-condensed-padding);
  border-radius: var(--p-stat-tile-condensed-border-radius);
}

.stat-tile--condensed .stat-tile__value {
  font-size: var(--p-stat-tile-condensed-value-font-size);
}

.stat-tile--condensed .stat-tile__label {
  font-size: var(--p-stat-tile-condensed-label-font-size);
}
</style>
